import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * Phase 14.1 — "Next Best Action" (NBA) module.
 *
 * Reusable end-of-page block that tells the visitor exactly where they are
 * in the journey and recommends one primary + one secondary next action.
 *
 * Steps are a fixed 5-stage model: Discover → Explore → Match → Connect → Plan
 *
 * Props:
 *   step        number 1..5 — the current step
 *   rationale   string — one-sentence "why this next action"
 *   primary     { label, to, external?, testid? }
 *   secondary?  { label, to, external?, testid? }
 *   pageId      string — identifier used in the analytics event payload
 */

export const NBA_STEPS = [
  { id: 1, label: "Discover" },
  { id: 2, label: "Explore" },
  { id: 3, label: "Match" },
  { id: 4, label: "Connect" },
  { id: 5, label: "Plan" },
];

const Dot = ({ state, label, idx }) => {
  const base =
    "inline-flex items-center justify-center rounded-full border transition-all duration-300";
  const style =
    state === "current"
      ? "w-5 h-5 bg-lw-gold border-lw-gold text-white shadow-[0_0_0_4px_rgba(212,162,76,0.22)]"
      : state === "done"
      ? "w-4 h-4 bg-emerald-500/90 border-emerald-500 text-white"
      : "w-3 h-3 bg-white border-lw-navy/25";
  return (
    <div className="flex flex-col items-center">
      <span className={`${base} ${style}`} aria-current={state === "current" ? "step" : undefined}>
        {state === "done" && <Check size={10} strokeWidth={3} aria-hidden="true" />}
      </span>
      <span
        className={`mt-2 text-[10px] uppercase tracking-[0.18em] ${
          state === "current"
            ? "text-lw-navy font-semibold"
            : state === "done"
            ? "text-emerald-700/80"
            : "text-lw-muted/70"
        }`}
      >
        {label}
      </span>
    </div>
  );
};

const NbaButton = ({ cta, variant, pageId, which }) => {
  if (!cta) return null;
  const common = {
    "data-testid": cta.testid || `nba-${which}`,
    onClick: () =>
      trackEvent("nba_cta_click", {
        page: pageId,
        which,
        destination: cta.to || cta.href,
      }),
  };
  const cls =
    variant === "primary"
      ? "btn-gold text-sm inline-flex items-center gap-2"
      : "btn-ghost-navy text-sm inline-flex items-center gap-2";
  if (cta.external) {
    return (
      <a href={cta.to} target="_blank" rel="noopener noreferrer" className={cls} {...common}>
        {cta.label} <ArrowRight size={14} />
      </a>
    );
  }
  return (
    <Link to={cta.to} className={cls} {...common}>
      {cta.label} <ArrowRight size={14} />
    </Link>
  );
};

export const NextBestAction = ({
  step,
  rationale,
  primary,
  secondary,
  pageId,
  eyebrow = "Your next best step",
  heading,
}) => {
  if (!primary) return null;
  return (
    <motion.section
      data-testid="next-best-action"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
      className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-16"
    >
      <div className="rounded-3xl border border-lw-gold/30 bg-white p-7 sm:p-10 relative overflow-hidden shadow-[0_28px_60px_-40px_rgba(10,37,64,0.35)]">
        <div
          aria-hidden="true"
          className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(212,162,76,0.14) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
        {/* Progress indicator */}
        <div
          data-testid="nba-progress"
          className="relative flex items-start justify-between gap-2 max-w-md mx-auto sm:mx-0 sm:max-w-sm mb-8"
        >
          {NBA_STEPS.map((s, idx) => {
            const state = s.id < step ? "done" : s.id === step ? "current" : "future";
            return (
              <React.Fragment key={s.id}>
                <Dot state={state} label={s.label} idx={idx} />
                {idx < NBA_STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={`flex-1 h-px mt-[9px] ${
                      s.id < step ? "bg-emerald-500/70" : "bg-lw-navy/15"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="relative max-w-2xl">
          <span className="lw-eyebrow">{eyebrow}</span>
          <h3
            data-testid="nba-heading"
            className="mt-3 font-serif-display text-2xl sm:text-3xl text-lw-navy leading-[1.15]"
          >
            {heading || primary.label}
          </h3>
          {rationale && (
            <p
              data-testid="nba-rationale"
              className="mt-3 text-sm sm:text-base text-lw-muted leading-relaxed"
            >
              {rationale}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <NbaButton cta={primary} variant="primary" pageId={pageId} which="primary" />
            <NbaButton cta={secondary} variant="secondary" pageId={pageId} which="secondary" />
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default NextBestAction;
